import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Radio,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  Tooltip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';

// --- TYPE DEFINITIONS ---
type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
type Currency = 'INR' | 'USD';
type Threshold = {
  id: number;
  percentage: string;
};

// --- MODIFIED PROPS INTERFACE ---
interface BudgetDetailsProps {
    period: BudgetPeriod;
    setPeriod: (p: BudgetPeriod) => void;
    currency: Currency;
    setCurrency: (c: Currency) => void;
    amount: string;
    setAmount: (a: string) => void;
    thresholds: Threshold[];
    setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
    errors: { [key: string]: string };
    includeCredits: boolean;
    setIncludeCredits: (include: boolean) => void;
    loading?: boolean;
    error?: any;
    refetch?: () => void;
    provider: string; // Added provider
    metric: 'COST' | 'TOKENS'; // Added metric
    setMetric: (m: 'COST' | 'TOKENS') => void; // Added metric setter
}

// --- HELPER FUNCTIONS ---
const parseAmount = (str: string): number => {
  return parseFloat(str.replace(/,/g, ''));
};

const formatAmount = (num: number): string => {
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
};

// --- Custom styled Switch component ---
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#03A65A',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 18,
    height: 18,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

// --- DataErrorState component ---
interface DataErrorStateProps {
    onRefresh: () => void;
}

const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                height: '100%',
                minHeight: '400px',
                p: 3,
                gap: 1,
            }}
        >
            <img 
                src="/icons/unable_to_diplay_data.svg" 
                alt="Hourglass indicating a temporary issue" 
                style={{ width: '120px', height: '120px' }} 
            />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
                Unable to Display Data
            </Typography>
            <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
                We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
            </Typography>
            <Button
                variant="text"
                onClick={onRefresh}
                sx={{
                    color: '#0064FF',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '14px',
                    marginTop: '2px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 100, 255, 0.04)'
                    }
                }}
            >
                Refresh Page
            </Button>
        </Box>
    );
};


// --- MAIN COMPONENT ---
const BudgetDetails: React.FC<BudgetDetailsProps> = ({
    period, setPeriod, currency, setCurrency, amount, setAmount,
    thresholds, setThresholds, errors, includeCredits, setIncludeCredits,
    loading, error, refetch, provider, metric, setMetric,
}) => {
  const [periodOptions] = useState([
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Yearly', label: 'Yearly' },
  ]);

  const visibleCurrencies = [
      { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
  ];

  useEffect(() => {
    const numericAmount = parseAmount(amount);
    if (!isNaN(numericAmount) && numericAmount > 0) {
      if (thresholds.length === 0) {
        setThresholds([
          { id: Date.now() + 1, percentage: '50' },
          { id: Date.now() + 2, percentage: '75' },
          { id: Date.now() + 3, percentage: '100' },
        ]);
      }
    } else {
      setThresholds([]);
    }
  }, [amount, thresholds.length, setThresholds]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (rawValue === '') {
      setAmount('');
      return;
    }
    const numericValue = parseAmount(rawValue);
    if (!isNaN(numericValue)) {
      setAmount(formatAmount(numericValue));
    }
  };

  const handleAddThreshold = () => {
    setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
  };

  const handleDeleteThreshold = (id: number) => {
    if (thresholds.length <= 1) {
      return;
    }
    setThresholds(thresholds.filter((t) => t.id !== id));
  };

  const handleThresholdChange = (id: number, newPercentage: string) => {
    const numericValue = newPercentage.replace(/\D/g, '');
    if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
      setThresholds(
        thresholds.map((t) =>
          t.id === id ? { ...t, percentage: numericValue } : t
        )
      );
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      height: '36px',
      paddingLeft: 0, // Let the InputAdornment handle the spacing
      borderRadius: '8px',
      fontFamily: 'Inter',
      backgroundColor: '#FFFFFF',
      '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
      '&.Mui-focused': { boxShadow: 'none' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' },
      '&.Mui-disabled': { backgroundColor: '#FFFFFF' },
    },
    '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', color: '#999999' },
    '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px' },
    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333333' },
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return <DataErrorState onRefresh={refetch || (() => window.location.reload())} />;
    }

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
            Budget Details
          </Typography>
        </Box>

        <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

        <Box sx={{ mt: 2 }}>
            {provider === 'LANGFUSE' && (
                <Box sx={{ mb: 2 }}>
                    <Typography component="label" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
                        Budget Metric<span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <FormControl>
                        <RadioGroup row value={metric} onChange={(e) => setMetric(e.target.value as 'COST' | 'TOKENS')}>
                            <FormControlLabel 
                                value="COST" 
                                control={
                                    <Radio 
                                        size="small" 
                                        sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} 
                                    />
                                } 
                                label={
                                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'COST' ? '#333333' : '#666666' }}>
                                        Cost
                                    </Typography>
                                } 
                            />
                            <FormControlLabel 
                                value="TOKENS" 
                                control={
                                    <Radio 
                                        size="small" 
                                        sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }}
                                    />
                                } 
                                label={
                                    <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'TOKENS' ? '#333333' : '#666666' }}>
                                        Tokens
                                    </Typography>
                                }
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            )}

           <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
            <Box>
              <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
                Budget Period<span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  id="budget-period-select" value={period}
                  onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => (<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontStyle: 'normal', fontSize: '12px', lineHeight: '18px', letterSpacing: '0%', color: '#333333', marginTop:"2px" }}>{selected}</Typography>)}
                  sx={{ height: '36px', borderRadius: '8px', backgroundColor: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' }, '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' }, }}
                  MenuProps={{ PaperProps: { sx: { borderRadius: '8px', marginTop: '4px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' } } }}
                >
                  {periodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ height: '40px', backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Radio checked={period === option.value} size="small" sx={{ py: 0, px: 1, paddingLeft:'0.25px', color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 400, color: period === option.value ? '#333333' : '#666666' }}>{option.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
                {metric === 'TOKENS' ? 'Budget Tokens (T)' : 'Budget Amount ($)'}<span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                id="budget-amount-input" fullWidth placeholder="e.g. 100,000"
                value={amount} onChange={handleAmountChange} error={!!errors.amount}
                helperText={errors.amount} sx={textFieldStyles}
                InputProps={{
                  startAdornment: (provider !== 'LANGFUSE' || metric === 'COST') ? (
                    <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 12px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default', }}>
                       {visibleCurrencies.length > 1 ? (
                          <Select value={currency} onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)} variant="standard" disableUnderline IconComponent={KeyboardArrowDownIcon} sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}>
                            {visibleCurrencies.map(c => (<MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}><img src={c.icon} alt={`${c.value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography></MenuItem>))}
                          </Select>
                       ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><img src={visibleCurrencies[0].icon} alt={`${visibleCurrencies[0].value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography></Box>
                       )}
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 16px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}>
                      <Typography component="span" sx={{ fontSize: '12px', fontWeight: 500, color: '#333333' }}>T</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            {provider !== 'LANGFUSE' && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '36px', p: '8px 16px', borderRadius: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F3F3' }}>
                    <CustomSwitch checked={includeCredits} onChange={(e) => setIncludeCredits(e.target.checked)} />
                    <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
                        Do you want to include credits?
                    </Typography>
                    <Tooltip title="When enabled, the budget will track your final cost after all discounts and credits are applied." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}>
                        <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
                    </Tooltip>
                </Box>
            )}
        </Box>

        {amount && (
          <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
              <img src="/icons/clock-icon.svg" alt="Clock Icon" width="22" height="22" />
            </Box>
            <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
              According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
            </Typography>
          </Box>
        )}

        <Box sx={{ maxWidth: { md: '696px' } }}>
          {thresholds.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
                  <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
                </Box>
                <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
                  Add More
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {thresholds.map((threshold) => {
                  const mainNumericAmount = parseAmount(amount);
                  const percentageNumeric = parseFloat(threshold.percentage);
                  
                  const adornmentSymbol = (provider !== 'LANGFUSE' || metric === 'COST') ? '$' : 'T';

                  const calculatedValue =
                    !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
                      ? (mainNumericAmount * percentageNumeric) / 100
                      : NaN;

                  const calculatedAmount = !isNaN(calculatedValue) ? formatAmount(calculatedValue) : '';
                  
                  const isLastThreshold = thresholds.length === 1;

                  return (
                    <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
                      <Box sx={{ flex: '1 1 40%' }}>
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
                        <TextField fullWidth value={threshold.percentage ? `${threshold.percentage}%` : ''} onChange={(e) => handleThresholdChange(threshold.id, e.target.value)} sx={textFieldStyles} />
                      </Box>
                      <Box sx={{ flex: '1 1 40%' }}>
                        <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>
                          {metric === 'TOKENS' ? 'Budget in Tokens' : 'Budget In Amount'}
                        </Typography>
                        <TextField 
                            fullWidth 
                            value={calculatedAmount} 
                            disabled 
                            sx={textFieldStyles} 
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{
                                            backgroundColor: '#FAFAFA',
                                            height: '36px',
                                            maxHeight: '36px',
                                            padding: '0 16px',
                                            borderRight: '1px solid #E6E6E6',
                                            borderTopLeftRadius: '8px',
                                            borderBottomLeftRadius: '8px',
                                        }}
                                    >
                                        <Typography component="span" sx={{ fontSize: '12px', fontWeight: 500, color: '#333333' }}>
                                            {adornmentSymbol}
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                        />
                      </Box>
                      <IconButton onClick={() => handleDeleteThreshold(threshold.id)} sx={{ mb: 0.5, ml: 'auto' }} disabled={isLastThreshold}>
                          <img src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete" style={{ width: '17px', height: '17px' }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
      {renderContent()}
    </Box>
  );
};

export default BudgetDetails;




// import React, { useEffect, useState } from 'react';
// import {
//   Box,
//   Typography,
//   Divider,
//   FormControl,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Button,
//   Radio,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
//   Switch,
//   Tooltip,
//   CircularProgress,
//   RadioGroup,
//   FormControlLabel,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import AddIcon from '@mui/icons-material/Add';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import { styled } from '@mui/material/styles';

// // --- TYPE DEFINITIONS ---
// type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
// type Currency = 'INR' | 'USD';
// type Threshold = {
//   id: number;
//   percentage: string;
// };

// // --- MODIFIED PROPS INTERFACE ---
// interface BudgetDetailsProps {
//     period: BudgetPeriod;
//     setPeriod: (p: BudgetPeriod) => void;
//     currency: Currency;
//     setCurrency: (c: Currency) => void;
//     amount: string;
//     setAmount: (a: string) => void;
//     thresholds: Threshold[];
//     setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
//     errors: { [key: string]: string };
//     includeCredits: boolean;
//     setIncludeCredits: (include: boolean) => void;
//     loading?: boolean;
//     error?: any;
//     refetch?: () => void;
//     provider: string; // Added provider
//     metric: 'COST' | 'TOKENS'; // Added metric
//     setMetric: (m: 'COST' | 'TOKENS') => void; // Added metric setter
// }

// // --- HELPER FUNCTIONS ---
// const parseAmount = (str: string): number => {
//   return parseFloat(str.replace(/,/g, ''));
// };

// const formatAmount = (num: number): string => {
//   if (isNaN(num)) return '';
//   return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
// };

// // --- Custom styled Switch component ---
// const CustomSwitch = styled(Switch)(({ theme }) => ({
//   width: 42,
//   height: 22,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 2,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(20px)',
//       color: '#fff',
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#03A65A',
//         opacity: 1,
//         border: 0,
//       },
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 18,
//     height: 18,
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 26 / 2,
//     backgroundColor: '#E9E9EA',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//   },
// }));

// // --- DataErrorState component ---
// interface DataErrorStateProps {
//     onRefresh: () => void;
// }

// const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 textAlign: 'center',
//                 height: '100%',
//                 minHeight: '400px',
//                 p: 3,
//                 gap: 1,
//             }}
//         >
//             <img 
//                 src="/icons/unable_to_diplay_data.svg" 
//                 alt="Hourglass indicating a temporary issue" 
//                 style={{ width: '120px', height: '120px' }} 
//             />
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
//                 Unable to Display Data
//             </Typography>
//             <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
//                 We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
//             </Typography>
//             <Button
//                 variant="text"
//                 onClick={onRefresh}
//                 sx={{
//                     color: '#0064FF',
//                     textTransform: 'none',
//                     fontWeight: 500,
//                     fontSize: '14px',
//                     marginTop: '2px',
//                     '&:hover': {
//                         backgroundColor: 'rgba(0, 100, 255, 0.04)'
//                     }
//                 }}
//             >
//                 Refresh Page
//             </Button>
//         </Box>
//     );
// };


// // --- MAIN COMPONENT ---
// const BudgetDetails: React.FC<BudgetDetailsProps> = ({
//     period, setPeriod, currency, setCurrency, amount, setAmount,
//     thresholds, setThresholds, errors, includeCredits, setIncludeCredits,
//     loading, error, refetch, provider, metric, setMetric,
// }) => {
//   const [periodOptions] = useState([
//     { value: 'Monthly', label: 'Monthly' },
//     { value: 'Quarterly', label: 'Quarterly' },
//     { value: 'Yearly', label: 'Yearly' },
//   ]);

//   const visibleCurrencies = [
//       { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
//   ];

//   useEffect(() => {
//     const numericAmount = parseAmount(amount);
//     if (!isNaN(numericAmount) && numericAmount > 0) {
//       if (thresholds.length === 0) {
//         setThresholds([
//           { id: Date.now() + 1, percentage: '50' },
//           { id: Date.now() + 2, percentage: '75' },
//           { id: Date.now() + 3, percentage: '100' },
//         ]);
//       }
//     } else {
//       setThresholds([]);
//     }
//   }, [amount, thresholds.length, setThresholds]);

//   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const rawValue = e.target.value;
//     if (rawValue === '') {
//       setAmount('');
//       return;
//     }
//     const numericValue = parseAmount(rawValue);
//     if (!isNaN(numericValue)) {
//       setAmount(formatAmount(numericValue));
//     }
//   };

//   const handleAddThreshold = () => {
//     setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
//   };

//   const handleDeleteThreshold = (id: number) => {
//     if (thresholds.length <= 1) {
//       return;
//     }
//     setThresholds(thresholds.filter((t) => t.id !== id));
//   };

//   const handleThresholdChange = (id: number, newPercentage: string) => {
//     const numericValue = newPercentage.replace(/\D/g, '');
//     if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
//       setThresholds(
//         thresholds.map((t) =>
//           t.id === id ? { ...t, percentage: numericValue } : t
//         )
//       );
//     }
//   };

//   const textFieldStyles = {
//     '& .MuiOutlinedInput-root': {
//       height: '36px',
//       paddingLeft: 0, // Let the InputAdornment handle the spacing
//       borderRadius: '8px',
//       fontFamily: 'Inter',
//       backgroundColor: '#FFFFFF',
//       '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
//       '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
//       '&.Mui-focused': { boxShadow: 'none' },
//       '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' },
//       '&.Mui-disabled': { backgroundColor: '#FFFFFF' },
//     },
//     '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', color: '#999999' },
//     '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px' },
//     '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333333' },
//   };

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
//           <CircularProgress />
//         </Box>
//       );
//     }
    
//     if (error) {
//       return <DataErrorState onRefresh={refetch || (() => window.location.reload())} />;
//     }

//     return (
//       <>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//           <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//           <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
//             Budget Details
//           </Typography>
//         </Box>

//         <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//         <Box sx={{ mt: 2 }}>
//             {provider === 'LANGFUSE' && (
//                 <Box sx={{ mb: 2 }}>
//                     <Typography component="label" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                         Budget Metric<span style={{ color: 'red' }}>*</span>
//                     </Typography>
//                     <FormControl>
//                         <RadioGroup row value={metric} onChange={(e) => setMetric(e.target.value as 'COST' | 'TOKENS')}>
//                             <FormControlLabel 
//                                 value="COST" 
//                                 control={
//                                     <Radio 
//                                         size="small" 
//                                         sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} 
//                                     />
//                                 } 
//                                 label={
//                                     <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'COST' ? '#333333' : '#666666' }}>
//                                         Cost
//                                     </Typography>
//                                 } 
//                             />
//                             <FormControlLabel 
//                                 value="TOKENS" 
//                                 control={
//                                     <Radio 
//                                         size="small" 
//                                         sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }}
//                                     />
//                                 } 
//                                 label={
//                                     <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'TOKENS' ? '#333333' : '#666666' }}>
//                                         Tokens
//                                     </Typography>
//                                 }
//                             />
//                         </RadioGroup>
//                     </FormControl>
//                 </Box>
//             )}

//            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
//             <Box>
//               <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Period<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <FormControl fullWidth>
//                 <Select
//                   id="budget-period-select" value={period}
//                   onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
//                   IconComponent={KeyboardArrowDownIcon}
//                   renderValue={(selected) => (<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontStyle: 'normal', fontSize: '12px', lineHeight: '18px', letterSpacing: '0%', color: '#333333', marginTop:"2px" }}>{selected}</Typography>)}
//                   sx={{ height: '36px', borderRadius: '8px', backgroundColor: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' }, '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' }, }}
//                   MenuProps={{ PaperProps: { sx: { borderRadius: '8px', marginTop: '4px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' } } }}
//                 >
//                   {periodOptions.map((option) => (
//                     <MenuItem key={option.value} value={option.value} sx={{ height: '40px', backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                         <Radio checked={period === option.value} size="small" sx={{ py: 0, px: 1, paddingLeft:'0.25px', color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} />
//                         <Typography sx={{ fontSize: '14px', fontWeight: 400, color: period === option.value ? '#333333' : '#666666' }}>{option.label}</Typography>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Box>
            
//             <Box>
//               <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 {metric === 'TOKENS' ? 'Budget Tokens (T)' : 'Budget Amount ($)'}<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <TextField
//                 id="budget-amount-input" fullWidth placeholder="e.g. 100,000"
//                 value={amount} onChange={handleAmountChange} error={!!errors.amount}
//                 helperText={errors.amount} sx={textFieldStyles}
//                 InputProps={{
//                   startAdornment: (provider !== 'LANGFUSE' || metric === 'COST') ? (
//                     <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 12px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default', }}>
//                        {visibleCurrencies.length > 1 ? (
//                           <Select value={currency} onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)} variant="standard" disableUnderline IconComponent={KeyboardArrowDownIcon} sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}>
//                             {visibleCurrencies.map(c => (<MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}><img src={c.icon} alt={`${c.value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography></MenuItem>))}
//                           </Select>
//                        ) : (
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><img src={visibleCurrencies[0].icon} alt={`${visibleCurrencies[0].value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography></Box>
//                        )}
//                     </InputAdornment>
//                   ) : (
//                     <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 16px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }}>
//                       <Typography component="span" sx={{ fontSize: '12px', fontWeight: 500, color: '#333333' }}>T</Typography>
//                     </InputAdornment>
//                   ),
//                 }}
//               />
//             </Box>
            
//             {provider !== 'LANGFUSE' && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '36px', p: '8px 16px', borderRadius: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F3F3' }}>
//                     <CustomSwitch checked={includeCredits} onChange={(e) => setIncludeCredits(e.target.checked)} />
//                     <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
//                         Do you want to include credits?
//                     </Typography>
//                     <Tooltip title="When enabled, the budget will track your final cost after all discounts and credits are applied." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}>
//                         <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
//                     </Tooltip>
//                 </Box>
//             )}
//         </Box>

//         {amount && (
//           <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
//             <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
//               <img src="/icons/clock-icon.svg" alt="Clock Icon" width="22" height="22" />
//             </Box>
//             <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
//               According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
//             </Typography>
//           </Box>
//         )}

//         <Box sx={{ maxWidth: { md: '696px' } }}>
//           {thresholds.length > 0 && (
//             <Box sx={{ mt: 3 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
//                   <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
//                 </Box>
//                 <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
//                   Add More
//                 </Button>
//               </Box>

//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                 {thresholds.map((threshold) => {
//                   const mainNumericAmount = parseAmount(amount);
//                   const percentageNumeric = parseFloat(threshold.percentage);
                  
//                   const currencySymbol = (provider !== 'LANGFUSE' || metric === 'COST') ? (currency === 'INR' ? '₹' : '$') : 'T';

//                   const calculatedValue =
//                     !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
//                       ? (mainNumericAmount * percentageNumeric) / 100
//                       : NaN;

//                   const calculatedAmount = !isNaN(calculatedValue) ? `${currencySymbol} ${formatAmount(calculatedValue)}`.trim() : '';
                  
//                   const isLastThreshold = thresholds.length === 1;

//                   return (
//                     <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
//                         <TextField fullWidth value={threshold.percentage ? `${threshold.percentage}%` : ''} onChange={(e) => handleThresholdChange(threshold.id, e.target.value)} sx={textFieldStyles} />
//                       </Box>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>
//                           {metric === 'TOKENS' ? 'Budget in Tokens' : 'Budget In Amount'}
//                         </Typography>
//                         <TextField fullWidth value={calculatedAmount} disabled sx={textFieldStyles} />
//                       </Box>
//                       <IconButton onClick={() => handleDeleteThreshold(threshold.id)} sx={{ mb: 0.5, ml: 'auto' }} disabled={isLastThreshold}>
//                           <img src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete" style={{ width: '17px', height: '17px' }} />
//                       </IconButton>
//                     </Box>
//                   );
//                 })}
//               </Box>
//             </Box>
//           )}
//         </Box>
//         </Box>
//       </>
//     );
//   };

//   return (
//     <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
//       {renderContent()}
//     </Box>
//   );
// };

// export default BudgetDetails;





// import React, { useEffect, useState } from 'react';
// import {
//   Box,
//   Typography,
//   Divider,
//   FormControl,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Button,
//   Radio,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
//   Switch,
//   Tooltip,
//   CircularProgress,
//   RadioGroup,
//   FormControlLabel,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import AddIcon from '@mui/icons-material/Add';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import { styled } from '@mui/material/styles';

// // --- TYPE DEFINITIONS ---
// type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
// type Currency = 'INR' | 'USD';
// type Threshold = {
//   id: number;
//   percentage: string;
// };

// // --- MODIFIED PROPS INTERFACE ---
// interface BudgetDetailsProps {
//     period: BudgetPeriod;
//     setPeriod: (p: BudgetPeriod) => void;
//     currency: Currency;
//     setCurrency: (c: Currency) => void;
//     amount: string;
//     setAmount: (a: string) => void;
//     thresholds: Threshold[];
//     setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
//     errors: { [key: string]: string };
//     includeCredits: boolean;
//     setIncludeCredits: (include: boolean) => void;
//     loading?: boolean;
//     error?: any;
//     refetch?: () => void;
//     provider: string; // Added provider
//     metric: 'COST' | 'TOKENS'; // Added metric
//     setMetric: (m: 'COST' | 'TOKENS') => void; // Added metric setter
// }

// // --- HELPER FUNCTIONS ---
// const parseAmount = (str: string): number => {
//   return parseFloat(str.replace(/,/g, ''));
// };

// const formatAmount = (num: number): string => {
//   if (isNaN(num)) return '';
//   return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
// };

// // --- Custom styled Switch component ---
// const CustomSwitch = styled(Switch)(({ theme }) => ({
//   width: 42,
//   height: 22,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 2,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(20px)',
//       color: '#fff',
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#03A65A',
//         opacity: 1,
//         border: 0,
//       },
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 18,
//     height: 18,
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 26 / 2,
//     backgroundColor: '#E9E9EA',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//   },
// }));

// // --- DataErrorState component ---
// interface DataErrorStateProps {
//     onRefresh: () => void;
// }

// const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 textAlign: 'center',
//                 height: '100%',
//                 minHeight: '400px',
//                 p: 3,
//                 gap: 1,
//             }}
//         >
//             <img 
//                 src="/icons/unable_to_diplay_data.svg" 
//                 alt="Hourglass indicating a temporary issue" 
//                 style={{ width: '120px', height: '120px' }} 
//             />
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
//                 Unable to Display Data
//             </Typography>
//             <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
//                 We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
//             </Typography>
//             <Button
//                 variant="text"
//                 onClick={onRefresh}
//                 sx={{
//                     color: '#0064FF',
//                     textTransform: 'none',
//                     fontWeight: 500,
//                     fontSize: '14px',
//                     marginTop: '2px',
//                     '&:hover': {
//                         backgroundColor: 'rgba(0, 100, 255, 0.04)'
//                     }
//                 }}
//             >
//                 Refresh Page
//             </Button>
//         </Box>
//     );
// };


// // --- MAIN COMPONENT ---
// const BudgetDetails: React.FC<BudgetDetailsProps> = ({
//     period, setPeriod, currency, setCurrency, amount, setAmount,
//     thresholds, setThresholds, errors, includeCredits, setIncludeCredits,
//     loading, error, refetch, provider, metric, setMetric,
// }) => {
//   const [periodOptions] = useState([
//     { value: 'Monthly', label: 'Monthly' },
//     { value: 'Quarterly', label: 'Quarterly' },
//     { value: 'Yearly', label: 'Yearly' },
//   ]);

//   const visibleCurrencies = [
//       { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
//   ];

//   useEffect(() => {
//     const numericAmount = parseAmount(amount);
//     if (!isNaN(numericAmount) && numericAmount > 0) {
//       if (thresholds.length === 0) {
//         setThresholds([
//           { id: Date.now() + 1, percentage: '50' },
//           { id: Date.now() + 2, percentage: '75' },
//           { id: Date.now() + 3, percentage: '100' },
//         ]);
//       }
//     } else {
//       setThresholds([]);
//     }
//   }, [amount, thresholds.length, setThresholds]);

//   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const rawValue = e.target.value;
//     if (rawValue === '') {
//       setAmount('');
//       return;
//     }
//     const numericValue = parseAmount(rawValue);
//     if (!isNaN(numericValue)) {
//       setAmount(formatAmount(numericValue));
//     }
//   };

//   const handleAddThreshold = () => {
//     setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
//   };

//   const handleDeleteThreshold = (id: number) => {
//     if (thresholds.length <= 1) {
//       return;
//     }
//     setThresholds(thresholds.filter((t) => t.id !== id));
//   };

//   const handleThresholdChange = (id: number, newPercentage: string) => {
//     const numericValue = newPercentage.replace(/\D/g, '');
//     if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
//       setThresholds(
//         thresholds.map((t) =>
//           t.id === id ? { ...t, percentage: numericValue } : t
//         )
//       );
//     }
//   };

//   const textFieldStyles = {
//     '& .MuiOutlinedInput-root': {
//       height: '36px',
//       paddingLeft: (provider === 'LANGFUSE' && metric === 'TOKENS') ? '14px' : 0,
//       borderRadius: '8px',
//       fontFamily: 'Inter',
//       backgroundColor: '#FFFFFF',
//       '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
//       '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
//       '&.Mui-focused': { boxShadow: 'none' },
//       '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' },
//       '&.Mui-disabled': { backgroundColor: '#FFFFFF' },
//     },
//     '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', color: '#999999' },
//     '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px' },
//     '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333333' },
//   };

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
//           <CircularProgress />
//         </Box>
//       );
//     }
    
//     if (error) {
//       return <DataErrorState onRefresh={refetch || (() => window.location.reload())} />;
//     }

//     return (
//       <>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//           <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//           <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
//             Budget Details
//           </Typography>
//         </Box>

//         <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//         <Box sx={{ mt: 2 }}>
//             {provider === 'LANGFUSE' && (
//                 <Box sx={{ mb: 2 }}>
//                     <Typography component="label" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                         Budget Metric<span style={{ color: 'red' }}>*</span>
//                     </Typography>
//                     <FormControl>
//                         <RadioGroup row value={metric} onChange={(e) => setMetric(e.target.value as 'COST' | 'TOKENS')}>
//                             <FormControlLabel 
//                                 value="COST" 
//                                 control={
//                                     <Radio 
//                                         size="small" 
//                                         sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} 
//                                     />
//                                 } 
//                                 label={
//                                     <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'COST' ? '#333333' : '#666666' }}>
//                                         Cost
//                                     </Typography>
//                                 } 
//                             />
//                             <FormControlLabel 
//                                 value="TOKENS" 
//                                 control={
//                                     <Radio 
//                                         size="small" 
//                                         sx={{ py: 0, px: 1, color: '#666666', '&.Mui-checked': { color: '#0064FF' } }}
//                                     />
//                                 } 
//                                 label={
//                                     <Typography sx={{ fontSize: '14px', fontWeight: 400, color: metric === 'TOKENS' ? '#333333' : '#666666' }}>
//                                         Tokens
//                                     </Typography>
//                                 }
//                             />
//                         </RadioGroup>
//                     </FormControl>
//                 </Box>
//             )}

//            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
//             <Box>
//               <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Period<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <FormControl fullWidth>
//                 <Select
//                   id="budget-period-select" value={period}
//                   onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
//                   IconComponent={KeyboardArrowDownIcon}
//                   renderValue={(selected) => (<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontStyle: 'normal', fontSize: '12px', lineHeight: '18px', letterSpacing: '0%', color: '#333333', marginTop:"2px" }}>{selected}</Typography>)}
//                   sx={{ height: '36px', borderRadius: '8px', backgroundColor: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' }, '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' }, }}
//                   MenuProps={{ PaperProps: { sx: { borderRadius: '8px', marginTop: '4px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' } } }}
//                 >
//                   {periodOptions.map((option) => (
//                     <MenuItem key={option.value} value={option.value} sx={{ height: '40px', backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                         <Radio checked={period === option.value} size="small" sx={{ py: 0, px: 1, paddingLeft:'0.25px', color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} />
//                         <Typography sx={{ fontSize: '14px', fontWeight: 400, color: period === option.value ? '#333333' : '#666666' }}>{option.label}</Typography>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Box>
            
//             <Box>
//               <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Amount<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <TextField
//                 id="budget-amount-input" fullWidth placeholder="e.g. 100,000"
//                 value={amount} onChange={handleAmountChange} error={!!errors.amount}
//                 helperText={errors.amount} sx={textFieldStyles}
//                 InputProps={{
//                   startAdornment: (provider !== 'LANGFUSE' || metric === 'COST') ? (
//                     <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 12px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default', }}>
//                        {visibleCurrencies.length > 1 ? (
//                           <Select value={currency} onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)} variant="standard" disableUnderline IconComponent={KeyboardArrowDownIcon} sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}>
//                             {visibleCurrencies.map(c => (<MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}><img src={c.icon} alt={`${c.value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography></MenuItem>))}
//                           </Select>
//                        ) : (
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><img src={visibleCurrencies[0].icon} alt={`${visibleCurrencies[0].value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography></Box>
//                        )}
//                     </InputAdornment>
//                   ) : null,
//                 }}
//               />
//             </Box>
            
//             {provider !== 'LANGFUSE' && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '36px', p: '8px 16px', borderRadius: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F3F3' }}>
//                     <CustomSwitch checked={includeCredits} onChange={(e) => setIncludeCredits(e.target.checked)} />
//                     <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
//                         Do you want to include credits?
//                     </Typography>
//                     <Tooltip title="When enabled, the budget will track your final cost after all discounts and credits are applied." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}>
//                         <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
//                     </Tooltip>
//                 </Box>
//             )}
//         </Box>

//         {amount && (
//           <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
//             <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
//               <img src="/icons/clock-icon.svg" alt="Clock Icon" width="22" height="22" />
//             </Box>
//             <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
//               According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
//             </Typography>
//           </Box>
//         )}

//         <Box sx={{ maxWidth: { md: '696px' } }}>
//           {thresholds.length > 0 && (
//             <Box sx={{ mt: 3 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
//                   <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
//                 </Box>
//                 <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
//                   Add More
//                 </Button>
//               </Box>

//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                 {thresholds.map((threshold) => {
//                   const mainNumericAmount = parseAmount(amount);
//                   const percentageNumeric = parseFloat(threshold.percentage);
                  
//                   const currencySymbol = (provider !== 'LANGFUSE' || metric === 'COST') ? (currency === 'INR' ? '₹' : '$') : '';

//                   const calculatedValue =
//                     !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
//                       ? (mainNumericAmount * percentageNumeric) / 100
//                       : NaN;

//                   const calculatedAmount = !isNaN(calculatedValue) ? `${currencySymbol} ${formatAmount(calculatedValue)}`.trim() : '';
                  
//                   const isLastThreshold = thresholds.length === 1;

//                   return (
//                     <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
//                         <TextField fullWidth value={threshold.percentage ? `${threshold.percentage}%` : ''} onChange={(e) => handleThresholdChange(threshold.id, e.target.value)} sx={textFieldStyles} />
//                       </Box>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Amount</Typography>
//                         <TextField fullWidth value={calculatedAmount} disabled sx={textFieldStyles} />
//                       </Box>
//                       <IconButton onClick={() => handleDeleteThreshold(threshold.id)} sx={{ mb: 0.5, ml: 'auto' }} disabled={isLastThreshold}>
//                           <img src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete" style={{ width: '17px', height: '17px' }} />
//                       </IconButton>
//                     </Box>
//                   );
//                 })}
//               </Box>
//             </Box>
//           )}
//         </Box>
//         </Box>
//       </>
//     );
//   };

//   return (
//     <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
//       {renderContent()}
//     </Box>
//   );
// };

// export default BudgetDetails;



// import React, { useEffect, useState } from 'react';
// import {
//   Box,
//   Typography,
//   Divider,
//   FormControl,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Button,
//   Radio,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
//   Switch,
//   Tooltip,
//   CircularProgress,
//   RadioGroup,
//   FormControlLabel,
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import AddIcon from '@mui/icons-material/Add';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import { styled } from '@mui/material/styles';

// // --- TYPE DEFINITIONS ---
// type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
// type Currency = 'INR' | 'USD';
// type Threshold = {
//   id: number;
//   percentage: string;
// };

// // --- MODIFIED PROPS INTERFACE ---
// interface BudgetDetailsProps {
//     period: BudgetPeriod;
//     setPeriod: (p: BudgetPeriod) => void;
//     currency: Currency;
//     setCurrency: (c: Currency) => void;
//     amount: string;
//     setAmount: (a: string) => void;
//     thresholds: Threshold[];
//     setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
//     errors: { [key: string]: string };
//     includeCredits: boolean;
//     setIncludeCredits: (include: boolean) => void;
//     loading?: boolean;
//     error?: any;
//     refetch?: () => void;
//     provider: string; // Added provider
//     metric: 'COST' | 'TOKENS'; // Added metric
//     setMetric: (m: 'COST' | 'TOKENS') => void; // Added metric setter
// }

// // --- HELPER FUNCTIONS ---
// const parseAmount = (str: string): number => {
//   return parseFloat(str.replace(/,/g, ''));
// };

// const formatAmount = (num: number): string => {
//   if (isNaN(num)) return '';
//   return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
// };

// // --- Custom styled Switch component ---
// const CustomSwitch = styled(Switch)(({ theme }) => ({
//   width: 42,
//   height: 22,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 2,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(20px)',
//       color: '#fff',
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#03A65A',
//         opacity: 1,
//         border: 0,
//       },
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 18,
//     height: 18,
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 26 / 2,
//     backgroundColor: '#E9E9EA',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//   },
// }));

// // --- DataErrorState component ---
// interface DataErrorStateProps {
//     onRefresh: () => void;
// }

// const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 textAlign: 'center',
//                 height: '100%',
//                 minHeight: '400px',
//                 p: 3,
//                 gap: 1,
//             }}
//         >
//             <img 
//                 src="/icons/unable_to_diplay_data.svg" 
//                 alt="Hourglass indicating a temporary issue" 
//                 style={{ width: '120px', height: '120px' }} 
//             />
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
//                 Unable to Display Data
//             </Typography>
//             <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
//                 We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
//             </Typography>
//             <Button
//                 variant="text"
//                 onClick={onRefresh}
//                 sx={{
//                     color: '#0064FF',
//                     textTransform: 'none',
//                     fontWeight: 500,
//                     fontSize: '14px',
//                     marginTop: '2px',
//                     '&:hover': {
//                         backgroundColor: 'rgba(0, 100, 255, 0.04)'
//                     }
//                 }}
//             >
//                 Refresh Page
//             </Button>
//         </Box>
//     );
// };


// // --- MAIN COMPONENT ---
// const BudgetDetails: React.FC<BudgetDetailsProps> = ({
//     period, setPeriod, currency, setCurrency, amount, setAmount,
//     thresholds, setThresholds, errors, includeCredits, setIncludeCredits,
//     loading, error, refetch, provider, metric, setMetric,
// }) => {
//   const [periodOptions] = useState([
//     { value: 'Monthly', label: 'Monthly' },
//     { value: 'Quarterly', label: 'Quarterly' },
//     { value: 'Yearly', label: 'Yearly' },
//   ]);

//   const visibleCurrencies = [
//       { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
//   ];

//   useEffect(() => {
//     const numericAmount = parseAmount(amount);
//     if (!isNaN(numericAmount) && numericAmount > 0) {
//       if (thresholds.length === 0) {
//         setThresholds([
//           { id: Date.now() + 1, percentage: '50' },
//           { id: Date.now() + 2, percentage: '75' },
//           { id: Date.now() + 3, percentage: '100' },
//         ]);
//       }
//     } else {
//       setThresholds([]);
//     }
//   }, [amount, thresholds.length, setThresholds]);

//   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const rawValue = e.target.value;
//     if (rawValue === '') {
//       setAmount('');
//       return;
//     }
//     const numericValue = parseAmount(rawValue);
//     if (!isNaN(numericValue)) {
//       setAmount(formatAmount(numericValue));
//     }
//   };

//   const handleAddThreshold = () => {
//     setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
//   };

//   const handleDeleteThreshold = (id: number) => {
//     if (thresholds.length <= 1) {
//       return;
//     }
//     setThresholds(thresholds.filter((t) => t.id !== id));
//   };

//   const handleThresholdChange = (id: number, newPercentage: string) => {
//     const numericValue = newPercentage.replace(/\D/g, '');
//     if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
//       setThresholds(
//         thresholds.map((t) =>
//           t.id === id ? { ...t, percentage: numericValue } : t
//         )
//       );
//     }
//   };

//   const textFieldStyles = {
//     '& .MuiOutlinedInput-root': {
//       height: '36px',
//       paddingLeft: (provider === 'LANGFUSE' && metric === 'TOKENS') ? '14px' : 0,
//       borderRadius: '8px',
//       fontFamily: 'Inter',
//       backgroundColor: '#FFFFFF',
//       '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
//       '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
//       '&.Mui-focused': { boxShadow: 'none' },
//       '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' },
//       '&.Mui-disabled': { backgroundColor: '#FFFFFF' },
//     },
//     '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontFamily: 'Inter', color: '#999999' },
//     '& .MuiInputBase-input': { fontFamily: 'Inter', fontSize: '14px' },
//     '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#333333' },
//   };

//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
//           <CircularProgress />
//         </Box>
//       );
//     }
    
//     if (error) {
//       return <DataErrorState onRefresh={refetch || (() => window.location.reload())} />;
//     }

//     return (
//       <>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//           <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//           <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
//             Budget Details
//           </Typography>
//         </Box>

//         <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//         <Box sx={{ mt: 2 }}>
//             {provider === 'LANGFUSE' && (
//                 <Box sx={{ mb: 2 }}>
//                     <Typography component="label" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                         Budget Metric<span style={{ color: 'red' }}>*</span>
//                     </Typography>
//                     <FormControl>
//                         <RadioGroup row value={metric} onChange={(e) => setMetric(e.target.value as 'COST' | 'TOKENS')}>
//                             <FormControlLabel value="COST" control={<Radio size="small" />} label="Cost" />
//                             <FormControlLabel value="TOKENS" control={<Radio size="small" />} label="Tokens" />
//                         </RadioGroup>
//                     </FormControl>
//                 </Box>
//             )}

//            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
//             <Box>
//               <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Period<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <FormControl fullWidth>
//                 <Select
//                   id="budget-period-select" value={period}
//                   onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
//                   IconComponent={KeyboardArrowDownIcon}
//                   renderValue={(selected) => (<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontStyle: 'normal', fontSize: '12px', lineHeight: '18px', letterSpacing: '0%', color: '#333333', marginTop:"2px" }}>{selected}</Typography>)}
//                   sx={{ height: '36px', borderRadius: '8px', backgroundColor: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' }, '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1px solid #0064FF' }, }}
//                   MenuProps={{ PaperProps: { sx: { borderRadius: '8px', marginTop: '4px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' } } }}
//                 >
//                   {periodOptions.map((option) => (
//                     <MenuItem key={option.value} value={option.value} sx={{ height: '40px', backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent' }}>
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                         <Radio checked={period === option.value} size="small" sx={{ py: 0, px: 1, paddingLeft:'0.25px', color: '#666666', '&.Mui-checked': { color: '#0064FF' } }} />
//                         <Typography sx={{ fontSize: '14px', fontWeight: 400, color: period === option.value ? '#333333' : '#666666' }}>{option.label}</Typography>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Box>
            
//             <Box>
//               <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Amount<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <TextField
//                 id="budget-amount-input" fullWidth placeholder="e.g. 100,000"
//                 value={amount} onChange={handleAmountChange} error={!!errors.amount}
//                 helperText={errors.amount} sx={textFieldStyles}
//                 InputProps={{
//                   startAdornment: (provider !== 'LANGFUSE' || metric === 'COST') ? (
//                     <InputAdornment position="start" sx={{ backgroundColor: '#FAFAFA', height: '36px', maxHeight: '36px', padding: '0 12px', borderRight: '1px solid #E6E6E6', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default', }}>
//                        {visibleCurrencies.length > 1 ? (
//                           <Select value={currency} onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)} variant="standard" disableUnderline IconComponent={KeyboardArrowDownIcon} sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}>
//                             {visibleCurrencies.map(c => (<MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}><img src={c.icon} alt={`${c.value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography></MenuItem>))}
//                           </Select>
//                        ) : (
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><img src={visibleCurrencies[0].icon} alt={`${visibleCurrencies[0].value} Flag`} style={{ width: 16, height: 16 }} /><Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography></Box>
//                        )}
//                     </InputAdornment>
//                   ) : null,
//                 }}
//               />
//             </Box>
            
//             {provider !== 'LANGFUSE' && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', height: '36px', p: '8px 16px', borderRadius: '8px', backgroundColor: '#F9FAFB', border: '1px solid #F3F3F3' }}>
//                     <CustomSwitch checked={includeCredits} onChange={(e) => setIncludeCredits(e.target.checked)} />
//                     <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
//                         Do you want to include credits?
//                     </Typography>
//                     <Tooltip title="When enabled, the budget will track your final cost after all discounts and credits are applied." placement="top" arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#333333', borderRadius: '8px', padding: '8px', boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)', border: '1px solid #E0E0E0' } }, arrow: { sx: { color: '#FFFFFF' } } }}>
//                         <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
//                     </Tooltip>
//                 </Box>
//             )}
//         </Box>

//         {amount && (
//           <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
//             <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
//               <img src="/icons/clock-icon.svg" alt="Clock Icon" width="22" height="22" />
//             </Box>
//             <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
//               According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
//             </Typography>
//           </Box>
//         )}

//         <Box sx={{ maxWidth: { md: '696px' } }}>
//           {thresholds.length > 0 && (
//             <Box sx={{ mt: 3 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
//                   <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
//                 </Box>
//                 <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
//                   Add More
//                 </Button>
//               </Box>

//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                 {thresholds.map((threshold) => {
//                   const mainNumericAmount = parseAmount(amount);
//                   const percentageNumeric = parseFloat(threshold.percentage);
                  
//                   const currencySymbol = (provider !== 'LANGFUSE' || metric === 'COST') ? (currency === 'INR' ? '₹' : '$') : '';

//                   const calculatedValue =
//                     !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
//                       ? (mainNumericAmount * percentageNumeric) / 100
//                       : NaN;

//                   const calculatedAmount = !isNaN(calculatedValue) ? `${currencySymbol} ${formatAmount(calculatedValue)}`.trim() : '';
                  
//                   const isLastThreshold = thresholds.length === 1;

//                   return (
//                     <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
//                         <TextField fullWidth value={threshold.percentage ? `${threshold.percentage}%` : ''} onChange={(e) => handleThresholdChange(threshold.id, e.target.value)} sx={textFieldStyles} />
//                       </Box>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Amount</Typography>
//                         <TextField fullWidth value={calculatedAmount} disabled sx={textFieldStyles} />
//                       </Box>
//                       <IconButton onClick={() => handleDeleteThreshold(threshold.id)} sx={{ mb: 0.5, ml: 'auto' }} disabled={isLastThreshold}>
//                           <img src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete" style={{ width: '17px', height: '17px' }} />
//                       </IconButton>
//                     </Box>
//                   );
//                 })}
//               </Box>
//             </Box>
//           )}
//         </Box>
//         </Box>
//       </>
//     );
//   };

//   return (
//     <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
//       {renderContent()}
//     </Box>
//   );
// };

// export default BudgetDetails;



// // import React, { useEffect, useState } from 'react';
// // import {
// //   Box,
// //   Typography,
// //   Divider,
// //   FormControl,
// //   TextField,
// //   InputAdornment,
// //   IconButton,
// //   Button,
// //   Radio,
// //   Select,
// //   MenuItem,
// //   SelectChangeEvent,
// //   Switch,
// //   Tooltip,
// // } from '@mui/material';
// // import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// // import AddIcon from '@mui/icons-material/Add';
// // import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// // import { styled } from '@mui/material/styles';

// // // --- TYPE DEFINITIONS ---
// // type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
// // type Currency = 'INR' | 'USD';
// // type Threshold = {
// //   id: number;
// //   percentage: string;
// // };

// // // --- PROPS INTERFACE ---
// // interface BudgetDetailsProps {
// //     period: BudgetPeriod;
// //     setPeriod: (p: BudgetPeriod) => void;
// //     currency: Currency;
// //     setCurrency: (c: Currency) => void;
// //     amount: string;
// //     setAmount: (a: string) => void;
// //     thresholds: Threshold[];
// //     setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
// //     errors: { [key: string]: string };
// //     includeCredits: boolean;
// //     setIncludeCredits: (include: boolean) => void;
// // }

// // // --- HELPER FUNCTIONS ---
// // const parseAmount = (str: string): number => {
// //   return parseFloat(str.replace(/,/g, ''));
// // };

// // const formatAmount = (num: number): string => {
// //   if (isNaN(num)) return '';
// //   return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
// // };

// // // --- Custom styled Switch component ---
// // const CustomSwitch = styled(Switch)(({ theme }) => ({
// //   width: 42,
// //   height: 22,
// //   padding: 0,
// //   '& .MuiSwitch-switchBase': {
// //     padding: 0,
// //     margin: 2,
// //     transitionDuration: '300ms',
// //     '&.Mui-checked': {
// //       transform: 'translateX(20px)',
// //       color: '#fff',
// //       '& + .MuiSwitch-track': {
// //         backgroundColor: '#03A65A',
// //         opacity: 1,
// //         border: 0,
// //       },
// //     },
// //   },
// //   '& .MuiSwitch-thumb': {
// //     boxSizing: 'border-box',
// //     width: 18,
// //     height: 18,
// //   },
// //   '& .MuiSwitch-track': {
// //     borderRadius: 26 / 2,
// //     backgroundColor: '#E9E9EA',
// //     opacity: 1,
// //     transition: theme.transitions.create(['background-color'], {
// //       duration: 500,
// //     }),
// //   },
// // }));


// // // --- COMPONENT START ---
// // const BudgetDetails: React.FC<BudgetDetailsProps> = ({
// //     period,
// //     setPeriod,
// //     currency,
// //     setCurrency,
// //     amount,
// //     setAmount,
// //     thresholds,
// //     setThresholds,
// //     errors,
// //     includeCredits,
// //     setIncludeCredits,
// // }) => {
// //   const [periodOptions] = useState([
// //     { value: 'Monthly', label: 'Monthly' },
// //     { value: 'Quarterly', label: 'Quarterly' },
// //     { value: 'Yearly', label: 'Yearly' },
// //   ]);

// //   // ✅ CONTROL SWITCH: Define which currencies are visible in the UI.
// //   // To re-enable INR, just add its object back to this array.
// //   const visibleCurrencies = [
// //       { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
// //       // { value: 'INR', label: 'INR (₹)', icon: '/icons/india-flag.svg' },
// //   ];

// //   useEffect(() => {
// //     const numericAmount = parseAmount(amount);
// //     if (!isNaN(numericAmount) && numericAmount > 0) {
// //       if (thresholds.length === 0) {
// //         setThresholds([
// //           { id: Date.now() + 1, percentage: '50' },
// //           { id: Date.now() + 2, percentage: '75' },
// //           { id: Date.now() + 3, percentage: '100' },
// //         ]);
// //       }
// //     } else {
// //       setThresholds([]);
// //     }
// //   }, [amount, thresholds.length, setThresholds]);

// //   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
// //     const rawValue = e.target.value;
// //     if (rawValue === '') {
// //       setAmount('');
// //       return;
// //     }
// //     const numericValue = parseAmount(rawValue);
// //     if (!isNaN(numericValue)) {
// //       setAmount(formatAmount(numericValue));
// //     }
// //   };

// //   const handleAddThreshold = () => {
// //     setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
// //   };

// //   const handleDeleteThreshold = (id: number) => {
// //     if (thresholds.length <= 1) {
// //       return;
// //     }
// //     setThresholds(thresholds.filter((t) => t.id !== id));
// //   };

// //   const handleThresholdChange = (id: number, newPercentage: string) => {
// //     const numericValue = newPercentage.replace(/\D/g, '');
// //     if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
// //       setThresholds(
// //         thresholds.map((t) =>
// //           t.id === id ? { ...t, percentage: numericValue } : t
// //         )
// //       );
// //     }
// //   };

// //   const textFieldStyles = {
// //     '& .MuiOutlinedInput-root': {
// //       height: '36px',
// //       paddingLeft: 0,
// //       borderRadius: '8px',
// //       fontFamily: 'Inter',
// //       backgroundColor: '#FFFFFF',
// //       '& .MuiOutlinedInput-notchedOutline': {
// //         border: '0.82px solid #E6E6E6',
// //       },
// //       '&:hover .MuiOutlinedInput-notchedOutline': {
// //         borderColor: '#0064FF',
// //       },
// //       '&.Mui-focused': {
// //         boxShadow: 'none',
// //       },
// //       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
// //         border: '1px solid #0064FF',
// //       },
// //       '&.Mui-disabled': {
// //         backgroundColor: '#FFFFFF',
// //       },
// //     },
// //     '& .MuiInputBase-input::placeholder': {
// //       fontSize: '14px',
// //       fontFamily: 'Inter',
// //       color: '#999999',
// //     },
// //     '& .MuiInputBase-input': {
// //       fontFamily: 'Inter',
// //       fontSize: '14px',
// //     },
// //     '& .MuiInputBase-input.Mui-disabled': {
// //       WebkitTextFillColor: '#333333',
// //     },
// //   };

// //   return (
// //     <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
// //       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
// //         <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
// //         <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
// //           Budget Details
// //         </Typography>
// //       </Box>

// //       <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

// //       <Box sx={{ mt: 2 }}>
// //         <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
// //             <Box>
// //               <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
// //                 Budget Period<span style={{ color: 'red' }}>*</span>
// //               </Typography>
// //               <FormControl fullWidth>
// //                 <Select
// //                   id="budget-period-select"
// //                   value={period}
// //                   onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
// //                   IconComponent={KeyboardArrowDownIcon}
// //                   renderValue={(selected) => (
// //                     <Typography sx={{
// //                         fontFamily: 'Inter',
// //                         fontWeight: 500,
// //                         fontStyle: 'normal',
// //                         fontSize: '12px',
// //                         lineHeight: '18px',
// //                         letterSpacing: '0%',
// //                         color: '#333333',
// //                         marginTop:"2px"
// //                     }}>
// //                         {selected}
// //                     </Typography>
// //                   )}
// //                   sx={{
// //                     height: '36px',
// //                     borderRadius: '8px',
// //                     backgroundColor: '#FFFFFF',
// //                     '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
// //                     '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' },
// //                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
// //                       border: '1px solid #0064FF',
// //                     },
// //                   }}
// //                   MenuProps={{
// //                     PaperProps: {
// //                       sx: {
// //                         borderRadius: '8px',
// //                         marginTop: '4px',
// //                         boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
// //                       },
// //                     },
// //                   }}
// //                 >
// //                   {periodOptions.map((option) => (
// //                     <MenuItem
// //                       key={option.value}
// //                       value={option.value}
// //                       sx={{
// //                         height: '40px',
// //                         backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent',
// //                       }}
// //                     >
// //                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
// //                         <Radio
// //                           checked={period === option.value}
// //                           size="small"
// //                           sx={{ 
// //                             py: 0, 
// //                             px: 1,
// //                             paddingLeft:'0.25px',
// //                             color: '#666666',
// //                             '&.Mui-checked': {
// //                               color: '#0064FF',
// //                             },
// //                           }}
// //                         />
// //                         <Typography
// //                            sx={{
// //                              fontSize: '14px',
// //                              fontWeight: 400,
// //                              color: period === option.value ? '#333333' : '#666666',
// //                            }}
// //                         >
// //                           {option.label}
// //                         </Typography>
// //                       </Box>
// //                     </MenuItem>
// //                   ))}
// //                 </Select>
// //               </FormControl>
// //             </Box>
            
// //             <Box>
// //               <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
// //                 Budget Amount<span style={{ color: 'red' }}>*</span>
// //               </Typography>
// //               <TextField
// //                 id="budget-amount-input"
// //                 fullWidth
// //                 placeholder="e.g. 100,000"
// //                 value={amount}
// //                 onChange={handleAmountChange}
// //                 error={!!errors.amount}
// //                 helperText={errors.amount}
// //                 sx={textFieldStyles}
// //                 InputProps={{
// //                   startAdornment: (
// //                     <InputAdornment
// //                       position="start"
// //                       sx={{
// //                         backgroundColor: '#FAFAFA',
// //                         height: '36px',
// //                         maxHeight: '36px',
// //                         padding: '0 12px',
// //                         borderRight: '1px solid #E6E6E6',
// //                         borderTopLeftRadius: '8px',
// //                         borderBottomLeftRadius: '8px',
// //                         // ✅ Conditionally set cursor to default if not a dropdown
// //                         cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default',
// //                       }}
// //                     >
// //                        {/* ✅ Conditionally render the currency selector */}
// //                        {visibleCurrencies.length > 1 ? (
// //                           <Select 
// //                             value={currency} 
// //                             onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)}
// //                             variant="standard" 
// //                             disableUnderline 
// //                             IconComponent={KeyboardArrowDownIcon} 
// //                             sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}
// //                           >
// //                             {visibleCurrencies.map(c => (
// //                                 <MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}>
// //                                     <img 
// //                                         src={c.icon}
// //                                         alt={`${c.value} Flag`}
// //                                         style={{ width: 16, height: 16 }} 
// //                                     />
// //                                     <Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography>
// //                                 </MenuItem>
// //                             ))}
// //                           </Select>
// //                        ) : (
// //                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
// //                               <img 
// //                                   src={visibleCurrencies[0].icon}
// //                                   alt={`${visibleCurrencies[0].value} Flag`} 
// //                                   style={{ width: 16, height: 16 }} 
// //                               />
// //                               <Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography>
// //                           </Box>
// //                        )}
// //                     </InputAdornment>
// //                   ),
// //                 }}
// //               />
// //             </Box>
            
// //             <Box
// //               sx={{
// //                 display: 'flex',
// //                 alignItems: 'center',
// //                 justifyContent: 'flex-start',
// //                 height: '36px',
// //                 p: '8px 16px',
// //                 borderRadius: '8px',
// //                 backgroundColor: '#F9FAFB',
// //                 border: '1px solid #F3F3F3',
// //               }}
// //             >
// //                 <CustomSwitch
// //                     checked={includeCredits}
// //                     onChange={(e) => setIncludeCredits(e.target.checked)}
// //                 />
// //                 <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
// //                     Do you want to include credits?
// //                 </Typography>
// //                       <Tooltip 
// //                           title="When enabled, the budget will track your final cost after all discounts and credits are applied."
// //                           placement="top"
// //                           arrow
// //                           componentsProps={{
// //                               tooltip: {
// //                                   sx: {
// //                                       backgroundColor: '#FFFFFF',
// //                                       color: '#333333',
// //                                       borderRadius: '8px',
// //                                       padding: '8px',
// //                                       boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)',
// //                                       border: '1px solid #E0E0E0'
// //                                   }
// //                               },
// //                               arrow: {
// //                                   sx: {
// //                                       color: '#FFFFFF'
// //                                   }
// //                               }
// //                           }}
// //                       >
// //                     <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
// //                 </Tooltip>
// //             </Box>
// //         </Box>

// //         {amount && (
// //           <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
// //             <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
// //               <img
// //                 src="/icons/clock-icon.svg"
// //                 alt="Clock Icon"
// //                 width="22"
// //                 height="22"
// //               />
// //             </Box>
// //             <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
// //               According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
// //             </Typography>
// //           </Box>
// //         )}

// //         <Box sx={{ maxWidth: { md: '696px' } }}>
// //           {thresholds.length > 0 && (
// //             <Box sx={{ mt: 3 }}>
// //               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
// //                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
// //                   <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
// //                   <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
// //                 </Box>
// //                 <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
// //                   Add More
// //                 </Button>
// //               </Box>

// //               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
// //                 {thresholds.map((threshold) => {
// //                   const mainNumericAmount = parseAmount(amount);
// //                   const percentageNumeric = parseFloat(threshold.percentage);
// //                   // ✅ This logic still works perfectly for both currencies!
// //                   const currencySymbol = currency === 'INR' ? '₹' : '$';
// //                   const calculatedValue =
// //                     !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
// //                       ? (mainNumericAmount * percentageNumeric) / 100
// //                       : NaN;

// //                   const calculatedAmount = !isNaN(calculatedValue) ? `${currencySymbol} ${formatAmount(calculatedValue)}` : '';
                  
// //                   const isLastThreshold = thresholds.length === 1;

// //                   return (
// //                     <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
// //                       <Box sx={{ flex: '1 1 40%' }}>
// //                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
// //                         <TextField
// //                           fullWidth
// //                           value={threshold.percentage ? `${threshold.percentage}%` : ''}
// //                           onChange={(e) => handleThresholdChange(threshold.id, e.target.value)}
// //                           sx={textFieldStyles}
// //                         />
// //                       </Box>
// //                       <Box sx={{ flex: '1 1 40%' }}>
// //                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Amount</Typography>
// //                         <TextField fullWidth value={calculatedAmount} disabled sx={textFieldStyles} />
// //                       </Box>
// //                       <IconButton
// //                         onClick={() => handleDeleteThreshold(threshold.id)}
// //                         sx={{ mb: 0.5, ml: 'auto' }}
// //                         disabled={isLastThreshold}
// //                       >
// //                           <img
// //                             src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"}
// //                             alt="Delete"
// //                             style={{ width: '17px', height: '17px' }}
// //                         />
// //                       </IconButton>
// //                     </Box>
// //                   );
// //                 })}
// //               </Box>
// //             </Box>
// //           )}
// //         </Box>
// //       </Box>
// //     </Box>
// //   );
// // };

// // export default BudgetDetails;


// import React, { useEffect, useState } from 'react';
// import {
//   Box,
//   Typography,
//   Divider,
//   FormControl,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Button,
//   Radio,
//   Select,
//   MenuItem,
//   SelectChangeEvent,
//   Switch,
//   Tooltip,
//   CircularProgress, // Import CircularProgress for the loading state
// } from '@mui/material';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import AddIcon from '@mui/icons-material/Add';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import { styled } from '@mui/material/styles';

// // --- TYPE DEFINITIONS ---
// type BudgetPeriod = 'Monthly' | 'Quarterly' | 'Yearly';
// type Currency = 'INR' | 'USD';
// type Threshold = {
//   id: number;
//   percentage: string;
// };

// // --- 1. UPDATE THE PROPS INTERFACE ---
// interface BudgetDetailsProps {
//     period: BudgetPeriod;
//     setPeriod: (p: BudgetPeriod) => void;
//     currency: Currency;
//     setCurrency: (c: Currency) => void;
//     amount: string;
//     setAmount: (a: string) => void;
//     thresholds: Threshold[];
//     setThresholds: React.Dispatch<React.SetStateAction<Threshold[]>>;
//     errors: { [key: string]: string };
//     includeCredits: boolean;
//     setIncludeCredits: (include: boolean) => void;
//     // Add props for loading, error, and refetching
//     loading?: boolean;
//     error?: any;
//     refetch?: () => void;
// }

// // --- HELPER FUNCTIONS ---
// const parseAmount = (str: string): number => {
//   return parseFloat(str.replace(/,/g, ''));
// };

// const formatAmount = (num: number): string => {
//   if (isNaN(num)) return '';
//   return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
// };

// // --- Custom styled Switch component ---
// const CustomSwitch = styled(Switch)(({ theme }) => ({
//   width: 42,
//   height: 22,
//   padding: 0,
//   '& .MuiSwitch-switchBase': {
//     padding: 0,
//     margin: 2,
//     transitionDuration: '300ms',
//     '&.Mui-checked': {
//       transform: 'translateX(20px)',
//       color: '#fff',
//       '& + .MuiSwitch-track': {
//         backgroundColor: '#03A65A',
//         opacity: 1,
//         border: 0,
//       },
//     },
//   },
//   '& .MuiSwitch-thumb': {
//     boxSizing: 'border-box',
//     width: 18,
//     height: 18,
//   },
//   '& .MuiSwitch-track': {
//     borderRadius: 26 / 2,
//     backgroundColor: '#E9E9EA',
//     opacity: 1,
//     transition: theme.transitions.create(['background-color'], {
//       duration: 500,
//     }),
//   },
// }));

// // --- 2. DEFINE THE ERROR STATE COMPONENT ---
// interface DataErrorStateProps {
//     onRefresh: () => void;
// }

// const DataErrorState: React.FC<DataErrorStateProps> = ({ onRefresh }) => {
//     return (
//         <Box
//             sx={{
//                 display: 'flex',
//                 flexDirection: 'column',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 textAlign: 'center',
//                 height: '100%',
//                 minHeight: '400px', // Ensure it takes up some space
//                 p: 3,
//                 gap: 1,
//             }}
//         >
//             {/* Replace with your actual SVG icon path */}
//             <img 
//                 src="/icons/unable_to_diplay_data.svg" 
//                 alt="Hourglass indicating a temporary issue" 
//                 style={{ width: '120px', height: '120px' }} 
//             />
//             <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333',fontSize:'16px' }}>
//                 Unable to Display Data
//             </Typography>
//             <Typography variant="body2" sx={{ color: '#333333', maxWidth: '415px',fontWeight:400,fontSize:'12px' }}>
//                 We’re currently experiencing a temporary issue displaying the data. Please try refreshing the page or check back shortly.
//             </Typography>
//             <Button
//                 variant="text"
//                 onClick={onRefresh}
//                 sx={{
//                     color: '#0064FF',
//                     textTransform: 'none',
//                     fontWeight: 500,
//                     fontSize: '14px',
//                     marginTop: '2px',
//                     '&:hover': {
//                         backgroundColor: 'rgba(0, 100, 255, 0.04)'
//                     }
//                 }}
//             >
//                 Refresh Page
//             </Button>
//         </Box>
//     );
// };


// // --- MAIN COMPONENT ---
// const BudgetDetails: React.FC<BudgetDetailsProps> = ({
//     period,
//     setPeriod,
//     currency,
//     setCurrency,
//     amount,
//     setAmount,
//     thresholds,
//     setThresholds,
//     errors,
//     includeCredits,
//     setIncludeCredits,
//     loading, 
//     error,
//     refetch,
// }) => {
//   const [periodOptions] = useState([
//     { value: 'Monthly', label: 'Monthly' },
//     { value: 'Quarterly', label: 'Quarterly' },
//     { value: 'Yearly', label: 'Yearly' },
//   ]);

//   const visibleCurrencies = [
//       { value: 'USD', label: 'USD ($)', icon: '/icons/us-flag.svg' },
//   ];

//   useEffect(() => {
//     const numericAmount = parseAmount(amount);
//     if (!isNaN(numericAmount) && numericAmount > 0) {
//       if (thresholds.length === 0) {
//         setThresholds([
//           { id: Date.now() + 1, percentage: '50' },
//           { id: Date.now() + 2, percentage: '75' },
//           { id: Date.now() + 3, percentage: '100' },
//         ]);
//       }
//     } else {
//       setThresholds([]);
//     }
//   }, [amount, thresholds.length, setThresholds]);

//   // ... (All your handler functions like handleAmountChange, handleAddThreshold etc. remain the same)
//   const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const rawValue = e.target.value;
//     if (rawValue === '') {
//       setAmount('');
//       return;
//     }
//     const numericValue = parseAmount(rawValue);
//     if (!isNaN(numericValue)) {
//       setAmount(formatAmount(numericValue));
//     }
//   };

//   const handleAddThreshold = () => {
//     setThresholds([...thresholds, { id: Date.now(), percentage: '' }]);
//   };

//   const handleDeleteThreshold = (id: number) => {
//     if (thresholds.length <= 1) {
//       return;
//     }
//     setThresholds(thresholds.filter((t) => t.id !== id));
//   };

//   const handleThresholdChange = (id: number, newPercentage: string) => {
//     const numericValue = newPercentage.replace(/\D/g, '');
//     if (/^\d{0,3}$/.test(numericValue) && +numericValue <= 100) {
//       setThresholds(
//         thresholds.map((t) =>
//           t.id === id ? { ...t, percentage: numericValue } : t
//         )
//       );
//     }
//   };

//   const textFieldStyles = {
//     '& .MuiOutlinedInput-root': {
//       height: '36px',
//       paddingLeft: 0,
//       borderRadius: '8px',
//       fontFamily: 'Inter',
//       backgroundColor: '#FFFFFF',
//       '& .MuiOutlinedInput-notchedOutline': {
//         border: '0.82px solid #E6E6E6',
//       },
//       '&:hover .MuiOutlinedInput-notchedOutline': {
//         borderColor: '#0064FF',
//       },
//       '&.Mui-focused': {
//         boxShadow: 'none',
//       },
//       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//         border: '1px solid #0064FF',
//       },
//       '&.Mui-disabled': {
//         backgroundColor: '#FFFFFF',
//       },
//     },
//     '& .MuiInputBase-input::placeholder': {
//       fontSize: '14px',
//       fontFamily: 'Inter',
//       color: '#999999',
//     },
//     '& .MuiInputBase-input': {
//       fontFamily: 'Inter',
//       fontSize: '14px',
//     },
//     '& .MuiInputBase-input.Mui-disabled': {
//       WebkitTextFillColor: '#333333',
//     },
//   };

//   // --- 3. ADD CONDITIONAL RENDERING LOGIC ---
//   // This logic will now handle the display for loading, error, and success states.
//   const renderContent = () => {
//     if (loading) {
//       return (
//         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
//           <CircularProgress />
//         </Box>
//       );
//     }
    
//     if (error) {
//       // The refresh button will attempt to refetch or reload the page as a fallback.
//       return <DataErrorState onRefresh={refetch || (() => window.location.reload())} />;
//     }

//     // This is the original content of your component
//     return (
//       <>
//         <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//           <img src="/icons/Budget_Details_1.svg" alt="Budget Details" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//           <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px',color:'#333333',fontFamily:'Inter',lineHeight:'20px' }}>        
//             Budget Details
//           </Typography>
//         </Box>

//         <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//         <Box sx={{ mt: 2 }}>
//           {/* ... all your form fields (Budget Period, Amount, Thresholds, etc.) go here ... */}
//            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, alignItems: 'flex-end' }}>
//             <Box>
//               <Typography component="label" htmlFor="budget-period-select" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Period<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <FormControl fullWidth>
//                 <Select
//                   id="budget-period-select"
//                   value={period}
//                   onChange={(e: SelectChangeEvent) => setPeriod(e.target.value as BudgetPeriod)}
//                   IconComponent={KeyboardArrowDownIcon}
//                   renderValue={(selected) => (
//                     <Typography sx={{
//                         fontFamily: 'Inter',
//                         fontWeight: 500,
//                         fontStyle: 'normal',
//                         fontSize: '12px',
//                         lineHeight: '18px',
//                         letterSpacing: '0%',
//                         color: '#333333',
//                         marginTop:"2px"
//                     }}>
//                         {selected}
//                     </Typography>
//                   )}
//                   sx={{
//                     height: '36px',
//                     borderRadius: '8px',
//                     backgroundColor: '#FFFFFF',
//                     '& .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #E6E6E6' },
//                     '&:hover .MuiOutlinedInput-notchedOutline': { border: '0.82px solid #0064FF' },
//                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                       border: '1px solid #0064FF',
//                     },
//                   }}
//                   MenuProps={{
//                     PaperProps: {
//                       sx: {
//                         borderRadius: '8px',
//                         marginTop: '4px',
//                         boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
//                       },
//                     },
//                   }}
//                 >
//                   {periodOptions.map((option) => (
//                     <MenuItem
//                       key={option.value}
//                       value={option.value}
//                       sx={{
//                         height: '40px',
//                         backgroundColor: period === option.value ? 'rgba(0, 100, 255, 0.08)' : 'transparent',
//                       }}
//                     >
//                       <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
//                         <Radio
//                           checked={period === option.value}
//                           size="small"
//                           sx={{ 
//                             py: 0, 
//                             px: 1,
//                             paddingLeft:'0.25px',
//                             color: '#666666',
//                             '&.Mui-checked': {
//                               color: '#0064FF',
//                             },
//                           }}
//                         />
//                         <Typography
//                            sx={{
//                              fontSize: '14px',
//                              fontWeight: 400,
//                              color: period === option.value ? '#333333' : '#666666',
//                            }}
//                         >
//                           {option.label}
//                         </Typography>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </Select>
//               </FormControl>
//             </Box>
            
//             <Box>
//               <Typography component="label" htmlFor="budget-amount-input" variant="body2" sx={{ mb: 1, display: 'block', fontSize: '14px', color: '#333333' }}>
//                 Budget Amount<span style={{ color: 'red' }}>*</span>
//               </Typography>
//               <TextField
//                 id="budget-amount-input"
//                 fullWidth
//                 placeholder="e.g. 100,000"
//                 value={amount}
//                 onChange={handleAmountChange}
//                 error={!!errors.amount}
//                 helperText={errors.amount}
//                 sx={textFieldStyles}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment
//                       position="start"
//                       sx={{
//                         backgroundColor: '#FAFAFA',
//                         height: '36px',
//                         maxHeight: '36px',
//                         padding: '0 12px',
//                         borderRight: '1px solid #E6E6E6',
//                         borderTopLeftRadius: '8px',
//                         borderBottomLeftRadius: '8px',
//                         cursor: visibleCurrencies.length > 1 ? 'pointer' : 'default',
//                       }}
//                     >
//                        {visibleCurrencies.length > 1 ? (
//                           <Select 
//                             value={currency} 
//                             onChange={(e: SelectChangeEvent) => setCurrency(e.target.value as Currency)}
//                             variant="standard" 
//                             disableUnderline 
//                             IconComponent={KeyboardArrowDownIcon} 
//                             sx={{ '& .MuiSelect-select': { p: '0 8px', display: 'flex', alignItems: 'center', gap: 0.5 } }}
//                           >
//                             {visibleCurrencies.map(c => (
//                                 <MenuItem key={c.value} value={c.value} sx={{display: 'flex', alignItems: 'center',fontFamily: 'Inter',fontSize: '12px'}}>
//                                     <img 
//                                         src={c.icon}
//                                         alt={`${c.value} Flag`}
//                                         style={{ width: 16, height: 16 }} 
//                                     />
//                                     <Typography component="span" sx={{ ml: 0.75, fontSize: '12px',fontWeight:500 }}>{c.label}</Typography>
//                                 </MenuItem>
//                             ))}
//                           </Select>
//                        ) : (
//                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                               <img 
//                                   src={visibleCurrencies[0].icon}
//                                   alt={`${visibleCurrencies[0].value} Flag`} 
//                                   style={{ width: 16, height: 16 }} 
//                               />
//                               <Typography component="span" sx={{ fontSize: '12px',fontWeight:500 }}>{visibleCurrencies[0].label}</Typography>
//                           </Box>
//                        )}
//                     </InputAdornment>
//                   ),
//                 }}
//               />
//             </Box>
            
//             <Box
//               sx={{
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'flex-start',
//                 height: '36px',
//                 p: '8px 16px',
//                 borderRadius: '8px',
//                 backgroundColor: '#F9FAFB',
//                 border: '1px solid #F3F3F3',
//               }}
//             >
//                 <CustomSwitch
//                     checked={includeCredits}
//                     onChange={(e) => setIncludeCredits(e.target.checked)}
//                 />
//                 <Typography sx={{ ml: 1.5, mr: 1, fontSize: '13px', color: '#666666', fontWeight: 500 }}>
//                     Do you want to include credits?
//                 </Typography>
//                       <Tooltip 
//                           title="When enabled, the budget will track your final cost after all discounts and credits are applied."
//                           placement="top"
//                           arrow
//                           componentsProps={{
//                               tooltip: {
//                                   sx: {
//                                       backgroundColor: '#FFFFFF',
//                                       color: '#333333',
//                                       borderRadius: '8px',
//                                       padding: '8px',
//                                       boxShadow: '0px 1px 8px 0px rgba(110, 110, 110, 0.24)',
//                                       border: '1px solid #E0E0E0'
//                                   }
//                               },
//                               arrow: {
//                                   sx: {
//                                       color: '#FFFFFF'
//                                   }
//                               }
//                           }}
//                       >
//                     <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '18px', cursor: 'pointer' }} />
//                 </Tooltip>
//             </Box>
//         </Box>

//         {amount && (
//           <Box sx={{ mt: 3, p: 2, display: 'flex', alignItems: 'center', backgroundColor: '#F6F4E5', border: '1px solid #A59500', borderRadius: '8px' }}>
//             <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
//               <img
//                 src="/icons/clock-icon.svg"
//                 alt="Clock Icon"
//                 width="22"
//                 height="22"
//               />
//             </Box>
//             <Typography variant="body2" sx={{ color: '#A59500', fontSize: '14px' }}>
//               According to your budget amount we have set default alert thresholds. So, you’re notified before your budget is exceeded.
//             </Typography>
//           </Box>
//         )}

//         <Box sx={{ maxWidth: { md: '696px' } }}>
//           {thresholds.length > 0 && (
//             <Box sx={{ mt: 3 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                   <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#333' }}>Alert Thresholds</Typography>
//                   <InfoOutlinedIcon sx={{ color: '#0064FF', fontSize: '20px', cursor: 'pointer' }} />
//                 </Box>
//                 <Button startIcon={<AddIcon />} onClick={handleAddThreshold} sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#0064FF' }}>
//                   Add More
//                 </Button>
//               </Box>

//               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
//                 {thresholds.map((threshold) => {
//                   const mainNumericAmount = parseAmount(amount);
//                   const percentageNumeric = parseFloat(threshold.percentage);
//                   const currencySymbol = currency === 'INR' ? '₹' : '$';
//                   const calculatedValue =
//                     !isNaN(mainNumericAmount) && !isNaN(percentageNumeric)
//                       ? (mainNumericAmount * percentageNumeric) / 100
//                       : NaN;

//                   const calculatedAmount = !isNaN(calculatedValue) ? `${currencySymbol} ${formatAmount(calculatedValue)}` : '';
                  
//                   const isLastThreshold = thresholds.length === 1;

//                   return (
//                     <Box key={threshold.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, p: 2, bgcolor: '#F9F9F9', borderRadius: '10px' }}>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Percentage</Typography>
//                         <TextField
//                           fullWidth
//                           value={threshold.percentage ? `${threshold.percentage}%` : ''}
//                           onChange={(e) => handleThresholdChange(threshold.id, e.target.value)}
//                           sx={textFieldStyles}
//                         />
//                       </Box>
//                       <Box sx={{ flex: '1 1 40%' }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize: '14px' }}>Budget In Amount</Typography>
//                         <TextField fullWidth value={calculatedAmount} disabled sx={textFieldStyles} />
//                       </Box>
//                       <IconButton
//                         onClick={() => handleDeleteThreshold(threshold.id)}
//                         sx={{ mb: 0.5, ml: 'auto' }}
//                         disabled={isLastThreshold}
//                       >
//                           <img
//                             src={isLastThreshold ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"}
//                             alt="Delete"
//                             style={{ width: '17px', height: '17px' }}
//                         />
//                       </IconButton>
//                     </Box>
//                   );
//                 })}
//               </Box>
//             </Box>
//           )}
//         </Box>
//         </Box>
//       </>
//     );
//   };

//   return (
//     <Box sx={{ p: 2, bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #F3F3F3', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)', fontFamily: 'Inter' }}>
//       {renderContent()}
//     </Box>
//   );
// };

// export default BudgetDetails;